<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import StarRating from '../parts/StarRating.vue';
import { formatDateTime } from '../helpers';

const props = defineProps({
  source: { type: Object, required: true },
});

const { t, locale } = useI18n();

const review = computed(() => props.source.review || {});
const store = computed(() => review.value.store || {});
const complaint = computed(() => review.value.complaint || {});

const CATALOG_CLASSES = {
  '2ГИС': 'bg-[#19AA1E] text-white',
  'Яндекс Карты': 'bg-[#FC3F1D] text-white',
  'Яндекс.Карты': 'bg-[#FC3F1D] text-white',
  Google: 'bg-[#4285F4] text-white',
  'Google Maps': 'bg-[#4285F4] text-white',
  Отзовик: 'bg-[#F68B1F] text-white',
  Zoon: 'bg-[#6A3BD1] text-white',
  Flamp: 'bg-[#2C9BD6] text-white',
};

const catalogClass = computed(
  () => CATALOG_CLASSES[review.value.catalog] || 'bg-n-slate-11 text-white'
);

const COMPLAINT_STYLES = {
  sent: {
    classes: 'bg-n-amber-2 border-n-amber-4 text-n-amber-11',
    icon: 'i-lucide-send',
  },
  accepted: {
    classes: 'bg-n-teal-2 border-n-teal-4 text-n-teal-11',
    icon: 'i-lucide-shield-check',
  },
  rejected: {
    classes: 'bg-n-ruby-2 border-n-ruby-4 text-n-ruby-11',
    icon: 'i-lucide-shield-x',
  },
};

const complaintStyle = computed(() => COMPLAINT_STYLES[complaint.value.status]);

const complaintLabel = computed(() =>
  t(
    `MULTICHAT.SOURCE.COMPLAINT.STATUSES.${String(complaint.value.status).toUpperCase()}`
  )
);

const complaintDetails = computed(() =>
  [complaint.value.reason, complaint.value.comment].filter(Boolean).join(' — ')
);

const storeLine = computed(() =>
  [store.value.city, store.value.address].filter(Boolean).join(', ')
);
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex flex-wrap items-center gap-2">
      <span
        v-if="review.catalog"
        class="inline-flex items-center gap-1 h-6 px-2 rounded-md text-[11px] font-bold"
        :class="catalogClass"
      >
        <span class="i-lucide-map-pin size-3" />
        {{ review.catalog }}
      </span>
      <StarRating
        v-if="review.rating"
        :rating="Number(review.rating)"
        size="md"
        show-value
      />
      <span class="flex-1" />
      <a
        v-if="review.url || source.url"
        :href="review.url || source.url"
        target="_blank"
        rel="noopener noreferrer"
        class="inline-flex items-center gap-1 text-xs font-medium text-n-blue-11 hover:underline"
      >
        {{ t('MULTICHAT.SOURCE.REVIEW.OPEN') }}
        <span class="i-lucide-external-link size-3" />
      </a>
    </div>
    <div class="flex items-center gap-2 text-xs text-n-slate-11 min-w-0">
      <span class="i-lucide-user-round size-3.5 flex-shrink-0" />
      <span v-if="review.author" class="font-medium text-n-slate-12 truncate">
        {{ review.author }}
      </span>
      <span v-if="review.published_at" class="text-n-slate-10">
        · {{ formatDateTime(review.published_at, locale) }}
      </span>
    </div>
    <div
      v-if="store.code || storeLine"
      class="flex items-start gap-2.5 p-3 rounded-lg bg-n-alpha-1 border border-n-weak"
    >
      <span
        class="grid flex-shrink-0 size-8 rounded-lg place-items-center bg-[#E30611] text-white"
      >
        <span class="i-lucide-store size-4" />
      </span>
      <div class="flex flex-col min-w-0 gap-0.5">
        <span class="text-[13px] font-semibold text-n-slate-12">
          {{ t('MULTICHAT.SOURCE.REVIEW.STORE') }}
          <span v-if="store.code" class="font-mono text-n-slate-11">
            {{ store.code }}
          </span>
        </span>
        <span v-if="storeLine" class="text-xs text-n-slate-11">
          {{ storeLine }}
        </span>
      </div>
    </div>
    <div
      v-if="complaintStyle"
      class="flex items-start gap-2 px-3 py-2 rounded-lg border text-xs"
      :class="complaintStyle.classes"
    >
      <span class="size-4 flex-shrink-0 mt-px" :class="complaintStyle.icon" />
      <div class="flex flex-col gap-0.5 min-w-0">
        <span class="font-semibold">
          {{ complaintLabel }}
          <span v-if="complaint.sent_at" class="font-normal opacity-80">
            · {{ formatDateTime(complaint.sent_at, locale) }}
          </span>
        </span>
        <span v-if="complaintDetails" class="opacity-90">
          {{ complaintDetails }}
        </span>
      </div>
    </div>
  </div>
</template>
